/**
 * Card component for displaying content in a contained card layout.
 *
 * A flexible card component with variant and padding options, including sub-components
 * for structured card layouts.
 *
 * @example
 * ```tsx
 * // Basic card
 * <Card>
 *   <CardHeader>
 *     <CardTitle>Title</CardTitle>
 *     <CardDescription>Description</CardDescription>
 *   </CardHeader>
 *   <CardContent>Content</CardContent>
 *   <CardFooter>Footer</CardFooter>
 * </Card>
 *
 * // With variant
 * <Card variant="elevated">Elevated card</Card>
 * <Card variant="subtle">Subtle card</Card>
 * <Card variant="emerald">Card with emerald accent</Card>
 * <Card variant="purple">Card with purple accent</Card>
 * <Card variant="gold">Card with gold accent</Card>
 * <Card variant="pink">Card with pink accent</Card>
 *
 * // With padding
 * <Card padding="compact">Compact padding</Card>
 * <Card padding="spacious">Spacious padding</Card>
 * ```
 */
'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';
import { createMotionComponent, MOTION_PRESETS } from '@/lib/cva-motion';

/**
 * CVA variants for Card component.
 *
 * Variants:
 * - default: Standard card at elevation 1
 * - elevated: Card lifted to elevation 3
 * - subtle: Hairline card with no cast shadow
 * - panel: Console surface (squarer radius, elevation 1)
 * - interactive: Console surface that lifts to elevation 2 on hover
 * - emerald: Card with emerald border accent
 * - purple: Card with purple border accent
 * - gold: Card with gold border accent
 * - pink: Card with pink border accent
 * - marketing: Solid marketing grid card (no decorative blur)
 * - marketingMuted: Light solid card on warm/tinted sections
 * - marketingDark: Frosted-solid card on dark hero sections (no heavy blur stack)
 *
 * Padding options:
 * - default: Standard padding (p-4 sm:p-6)
 * - compact: Reduced padding (p-2 sm:p-4)
 * - spacious: Increased padding (p-4 sm:p-6 lg:p-8)
 * - flush: No outer padding (use with CardHeader / composed sections)
 */
const cardVariants = cva('rounded-lg border bg-card text-card-foreground transition-colors', {
  variants: {
    variant: {
      default: 'shadow-e1',
      elevated: 'shadow-e3 border-border',
      subtle: 'shadow-e0 border-border',
      /** Console surface: squarer radius reads as application chrome, not marketing card */
      panel: 'rounded-xl border-border/70 shadow-e1',
      /** Clickable console surface — lifts on hover, keeps depth from the elevation scale */
      interactive:
        'rounded-xl border-border/70 shadow-e1 transition-[box-shadow,border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-border hover:shadow-e2 focus-within:border-primary/40',
      emerald: 'shadow-e1 border-emerald/30 hover:border-emerald/50',
      purple: 'shadow-e1 border-purple/30 hover:border-purple/50',
      gold: 'shadow-e1 border-gold/30 hover:border-gold/50',
      pink: 'shadow-e1 border-pink/30 hover:border-pink/50',
      marketing:
        'rounded-3xl border border-border/50 bg-card shadow-e1 overflow-hidden transition-shadow duration-300 hover:shadow-e2',
      marketingMuted:
        'rounded-3xl border border-oxford-blue/15 bg-white/95 text-card-foreground shadow-e1 overflow-hidden transition-shadow duration-300 hover:shadow-e2',
      marketingDark:
        'rounded-3xl border border-white/15 bg-white/[0.07] text-white shadow-e1 overflow-hidden transition-shadow duration-300 hover:shadow-e2',
    },
    padding: {
      default: 'p-4 sm:p-6',
      compact: 'p-2 sm:p-4',
      spacious: 'p-4 sm:p-6 lg:p-8',
      flush: 'p-0',
    },
  },
  defaultVariants: {
    variant: 'default',
    padding: 'default',
  },
});

/**
 * Props for the Card component.
 *
 * Extends standard HTML div attributes and adds variant and padding props.
 *
 * @property variant - Visual style variant (default, elevated, subtle, emerald, purple, gold, pink)
 * @property padding - Padding variant (default, compact, spacious)
 */
export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardVariants> {}

/**
 * Card component with variant and padding support.
 *
 * Features:
 * - Type-safe variants using CVA
 * - Responsive padding options
 * - Shadow variants for depth
 * - Semantic card styling
 *
 * @param props - Card props including variant, padding, and standard HTML div attributes
 * @returns A styled card element
 */
const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, padding, ...props }, ref) => (
    <div ref={ref} className={cn(cardVariants({ variant, padding }), className)} {...props} />
  ),
);
Card.displayName = 'Card';

/**
 * CardHeader component for card header section.
 *
 * Provides consistent spacing and layout for card titles and descriptions.
 * Responsive padding: p-3 sm:p-4 lg:p-6
 */
const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex flex-col space-y-1.5 p-3 sm:p-4 lg:p-6', className)}
      {...props}
    />
  ),
);
CardHeader.displayName = 'CardHeader';

/**
 * CardTitle component for card titles.
 *
 * Renders as h3 with heading-3 typography class.
 * Defaults to "Untitled" if no children provided.
 */
const CardTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, children, ...props }, ref) => (
    <h3 ref={ref} className={cn('heading-3', className)} {...props}>
      {children || 'Untitled'}
    </h3>
  ),
);
CardTitle.displayName = 'CardTitle';

/**
 * CardDescription component for card descriptions.
 *
 * Renders as paragraph with body-small typography and muted foreground color.
 */
const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('body-small text-muted-foreground', className)} {...props} />
));
CardDescription.displayName = 'CardDescription';

/**
 * CardContent component for main card content.
 *
 * Provides consistent padding with responsive spacing.
 * Padding: p-3 sm:p-4 lg:p-6 pt-0 (no top padding to connect with header)
 */
const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-3 sm:p-4 lg:p-6 pt-0', className)} {...props} />
  ),
);
CardContent.displayName = 'CardContent';

/**
 * CardFooter component for card footer section.
 *
 * Provides consistent spacing and flex layout for footer content.
 * Padding: p-3 sm:p-4 lg:p-6 pt-0 (no top padding to connect with content)
 */
const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-center p-3 sm:p-4 lg:p-6 pt-0', className)}
      {...props}
    />
  ),
);
CardFooter.displayName = 'CardFooter';

/**
 * Motion-enhanced Card component with automatic animations.
 *
 * Provides the same API as Card but with built-in motion animations:
 * - Lift effect on hover
 * - Scale down on tap
 * - Smooth initial mount animation
 *
 * @example
 * ```tsx
 * <MotionCard variant="emerald" padding="spacious">
 *   <CardHeader>
 *     <CardTitle>Animated Card</CardTitle>
 *   </CardHeader>
 * </MotionCard>
 * ```
 */
const MotionCard = createMotionComponent(cardVariants, MOTION_PRESETS.card);

MotionCard.displayName = 'MotionCard';

/** Marketing section cards: solid surfaces + lift (see `MarketingSectionCard`) */
const MarketingMotionCard = createMotionComponent(cardVariants, MOTION_PRESETS.marketingCard);

MarketingMotionCard.displayName = 'MarketingMotionCard';

export {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  MarketingMotionCard,
  MotionCard,
  cardVariants,
};
